import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or signed number on the wire. `amount` is signed (Dr+ / Cr-) so we cannot
// use the non-negative variant from lva.ts.
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Enums
// ============================================================
export const GlScheduleWatchTypeSchema = z.enum(['OPEN_ITEM', 'RECON_ONLY']);
export const GlScheduleItemStatusSchema = z.enum(['OPEN', 'MATCHED']);
export const GlMatchKeySourceSchema = z.enum(['REF_KEY', 'PARTY', 'NONE']);
export const GlMatchTypeSchema = z.enum(['EXACT', 'AUTO', 'PARTIAL', 'RESIDUAL', 'WRITE_OFF']);
export const GlFollowUpStatusSchema = z.enum(['NONE', 'IN_PROGRESS', 'ESCALATED', 'RESOLVED']);
// ============================================================
// GlScheduleAccount — watched COA row
// ============================================================
export const GlScheduleAccountSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    accountCode: z.string().min(1),
    description: z.string().min(1),
    watchType: GlScheduleWatchTypeSchema,
    isActive: z.boolean(),
    requireRefKey: z.boolean(),
    matchKeySource: GlMatchKeySourceSchema,
    autoClear: z.boolean(),
    toleranceAmount: SignedDecimalLike.nullable().optional(),
    tolerancePct: SignedDecimalLike.nullable().optional(),
    writeOffAccountCode: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// POST /gl-schedule/accounts — create-watch payload. `watchType` defaults to
// OPEN_ITEM, matching the prototype's "Match by Ref (Auto)" clearing rule.
export const CreateGlScheduleAccountSchema = z.object({
    accountCode: z.string().min(1),
    description: z.string().min(1),
    watchType: GlScheduleWatchTypeSchema.default('OPEN_ITEM'),
    isActive: z.boolean().default(true),
    requireRefKey: z.boolean().default(false),
    matchKeySource: GlMatchKeySourceSchema.default('REF_KEY'),
    autoClear: z.boolean().default(false),
    toleranceAmount: SignedDecimalLike.nullable().optional(),
    tolerancePct: SignedDecimalLike.nullable().optional(),
    writeOffAccountCode: z.string().nullable().optional(),
});
// ============================================================
// GlScheduleItem — one row per watched JournalLine
// ============================================================
export const GlScheduleItemSchema = z.object({
    id: z.uuid(),
    accountId: z.uuid(),
    journalLineId: z.uuid(),
    refKey: z.string(),
    party: z.string().nullable().optional(),
    amount: SignedDecimalLike,
    status: GlScheduleItemStatusSchema,
    matchedAt: z.coerce.date().nullable().optional(),
    matchedBy: z.string().nullable().optional(),
    clearingDate: z.coerce.date().nullable().optional(),
    matchedToLineId: z.uuid().nullable().optional(),
    matchId: z.uuid().nullable().optional(),
    matchKey: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    dueDate: z.coerce.date().nullable().optional(),
    assigneeId: z.uuid().nullable().optional(),
    assigneeName: z.string().nullable().optional(),
    followUpStatus: GlFollowUpStatusSchema.optional(),
    reasonCode: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — joined display fields for the schedule grid. accountCode +
// voucherNo + voucherDate come from the FK chain through journal_lines.
export const GlScheduleItemListRowSchema = GlScheduleItemSchema.extend({
    accountCode: z.string(),
    voucherNo: z.string().nullable().optional(),
    voucherDate: z.coerce.date().nullable().optional(),
    description: z.string().nullable().optional(),
    ageDays: z.number().int(),
    entryId: z.uuid().nullable().optional(),
    openAsOf: z.boolean().optional(),
});
// ============================================================
// POST /gl-schedule/sync — ingest unseen journal_lines
// ============================================================
export const SyncGlScheduleSchema = z.object({
    // Optional — when present, sync only that watched account. Otherwise loops
    // over every active GlScheduleAccount for the company.
    accountCode: z.string().min(1).optional(),
});
export const SyncGlScheduleResponseSchema = z.object({
    inserted: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    accountsScanned: z.number().int().nonnegative(),
});
// ============================================================
// POST /gl-schedule/match — match selected items (sum must equal 0)
// ============================================================
export const MatchGlScheduleSchema = z.object({
    itemIds: z.array(z.uuid()).min(2, 'At least two items required to match'),
    notes: z.string().optional(),
});
// ============================================================
// PATCH /gl-schedule/items/:id/notes
// ============================================================
export const UpdateGlScheduleItemNotesSchema = z.object({
    notes: z.string(),
});
// ============================================================
// Filter shape for list endpoint
// ============================================================
export const ListGlScheduleItemsQuerySchema = z.object({
    accountCode: z.string().min(1).optional(),
    status: GlScheduleItemStatusSchema.optional(),
    asOf: z.iso.date().optional(),
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
});
// ============================================================
// PATCH /gl-schedule/accounts/:id — partial update (M1 subset)
// ============================================================
export const UpdateGlScheduleAccountSchema = z.object({
    description: z.string().min(1).optional(),
    isActive: z.boolean().optional(),
    watchType: GlScheduleWatchTypeSchema.optional(),
    requireRefKey: z.boolean().optional(),
    matchKeySource: GlMatchKeySourceSchema.optional(),
    autoClear: z.boolean().optional(),
    toleranceAmount: SignedDecimalLike.nullable().optional(),
    tolerancePct: SignedDecimalLike.nullable().optional(),
    writeOffAccountCode: z.string().nullable().optional(),
});
// ============================================================
// M3: follow-up / write-off / partial
// ============================================================
export const UpdateGlScheduleFollowUpSchema = z.object({
    dueDate: z.iso.date().nullable().optional(),
    assigneeId: z.uuid().nullable().optional(),
    assigneeName: z.string().nullable().optional(),
    followUpStatus: GlFollowUpStatusSchema.optional(),
    reasonCode: z.string().nullable().optional(),
});
export const WriteOffGlScheduleSchema = z.object({
    itemIds: z.array(z.uuid()).min(1, 'At least one item required to write off'),
    reasonCode: z.string().min(1, 'Reason code required'),
});
export const PartialMatchGlScheduleSchema = z.object({
    itemIds: z.array(z.uuid()).min(2, 'At least two items required'),
    notes: z.string().optional(),
});
export const WriteOffResultSchema = z.object({
    matchId: z.uuid(),
    writeOffJournalEntryId: z.uuid(),
    itemsCleared: z.number().int(),
});
// ============================================================
// GET /gl-schedule/reconciliation — per-account tie-out as of a key date
// ============================================================
export const ReconciliationRowSchema = z.object({
    accountCode: z.string(),
    glBalance: SignedDecimalLike, // independent: Σ(debit−credit) of POSTED lines ≤ keyDate
    scheduleOpenSum: SignedDecimalLike, // Σ amount of items OPEN as of keyDate
    delta: SignedDecimalLike, // glBalance − scheduleOpenSum
    withinTolerance: z.boolean(),
    openCount: z.number().int(),
    oldCount: z.number().int(), // age > 60 days as of keyDate
    avgAge: z.number().int(),
});
export const GlReconciliationQuerySchema = z.object({
    period: z
        .string()
        .regex(/^\d{6}$/)
        .optional(), // YYYYMM → month-end key date
    asOf: z.iso.date().optional(), // explicit key date (wins over period)
});
// ============================================================
// M2: Auto-clear — POST /gl-schedule/auto-clear
// ============================================================
export const AutoClearGlScheduleSchema = z.object({
    accountCode: z.string().min(1).optional(),
    dryRun: z.boolean().default(false),
});
export const AutoClearGroupSchema = z.object({
    accountCode: z.string(),
    matchKey: z.string(),
    itemIds: z.array(z.uuid()),
    total: SignedDecimalLike,
});
export const AutoClearResultSchema = z.object({
    groupsCleared: z.number().int(),
    itemsCleared: z.number().int(),
    groups: z.array(AutoClearGroupSchema),
});
// ============================================================
// M2: Reset match — POST /gl-schedule/matches/:id/reset
// ============================================================
export const ResetMatchResultSchema = z.object({ matchId: z.uuid(), reopened: z.number().int() });
// ============================================================
// M4: Period-close snapshot / certify / lock
// ============================================================
export const GlSnapshotStatusSchema = z.enum(['DRAFT', 'CERTIFIED', 'LOCKED']);
export const GlScheduleSnapshotSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string(),
    payload: z.unknown(),
    totalDelta: SignedDecimalLike,
    allReconciled: z.boolean(),
    status: GlSnapshotStatusSchema,
    preparedBy: z.string(),
    preparedAt: z.coerce.date(),
    certifiedBy: z.string().nullable().optional(),
    certifiedAt: z.coerce.date().nullable().optional(),
    lockedBy: z.string().nullable().optional(),
    lockedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const CreateGlScheduleSnapshotSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
});
//# sourceMappingURL=gl-schedule.js.map